import type { Actor, CollectionDef } from './collection.ts';
import { isAdultRole, type AccessFields } from './records.ts';

/**
 * The permission model, shared verbatim by the server (authoritative) and the
 * client (to hide actions a user cannot take). Keep it small enough to explain
 * in a sentence per rule:
 *
 *  1. You can always see what you own.
 *  2. "Everyone" is the whole family, "Grown-ups" excludes children,
 *     "Selected people" is exactly the listed members, "Only me" is only you.
 *  3. Adults can edit shared things in collaborative areas; authored content
 *     (comments, memories, notices) is edited only by its author.
 *  4. Children can add to shared lists/albums and complete their chores, and
 *     manage what they created themselves.
 *  5. Only the owner changes who can see something. If the owner has left the
 *     family, managers take over that responsibility.
 *  6. Managers can remove any shared item (moderation); deletions are
 *     soft and can be undone.
 */

export function canRead(actor: Actor, rec: AccessFields): boolean {
  if (rec.ownerId !== null && rec.ownerId === actor.userId) return true;
  switch (rec.audience) {
    case 'family':
      return true;
    case 'adults':
      return isAdultRole(actor.role);
    case 'selected':
      return rec.sharedWith.includes(actor.userId);
    case 'private':
      return false;
  }
}

/** Owner, or a manager standing in for a departed owner. */
export function isEffectiveOwner(actor: Actor, rec: AccessFields): boolean {
  if (rec.ownerId === null) return actor.role === 'manager';
  return rec.ownerId === actor.userId;
}

export function canEdit(actor: Actor, def: CollectionDef, rec: AccessFields): boolean {
  if (!canRead(actor, rec)) return false;
  if (def.editPolicy === 'author') return rec.createdBy === actor.userId;
  if (actor.role === 'child') {
    if (def.childAccess !== 'contribute') return false;
    // Children manage their own records and items inside shared containers
    // (e.g. checking off groceries) but don't rename the family's lists.
    return rec.createdBy === actor.userId || def.parent !== undefined;
  }
  return true;
}

export function canDelete(actor: Actor, def: CollectionDef, rec: AccessFields): boolean {
  if (!canRead(actor, rec)) return false;
  if (rec.createdBy === actor.userId) {
    return actor.role !== 'child' || def.childAccess === 'contribute';
  }
  // Owners (for child records: the owner of the container, e.g. the person
  // whose event a comment is on) can always tidy up their own things.
  if (isEffectiveOwner(actor, rec)) return true;
  if (actor.role === 'manager') return true;
  if (actor.role === 'child') {
    return def.childAccess === 'contribute' && def.parent !== undefined && def.editPolicy === 'collaborative';
  }
  return def.editPolicy === 'collaborative';
}

/** Only top-level records have their own audience; children inherit it. */
export function canChangeAudience(actor: Actor, def: CollectionDef, rec: AccessFields): boolean {
  if (def.parent) return false;
  return isEffectiveOwner(actor, rec);
}

/**
 * Whether the actor may add something to a parent container (an item to a
 * list, a photo to an album). Comments and chore completions override this via
 * `canCreate` on their collection.
 */
export function canContributeTo(actor: Actor, parentDef: CollectionDef, parent: AccessFields): boolean {
  if (!canRead(actor, parent)) return false;
  if (actor.role === 'child') return parentDef.childAccess === 'contribute';
  return parentDef.editPolicy === 'collaborative' || isEffectiveOwner(actor, parent);
}

export function canCreate(
  actor: Actor,
  def: CollectionDef,
  data: Record<string, unknown>,
  parent?: { def: CollectionDef; record: AccessFields & Record<string, unknown> },
): boolean {
  if (actor.role === 'child' && def.childAccess !== 'contribute') return false;
  if (def.parent) {
    if (!parent) return false;
    if (def.canCreate) return canRead(actor, parent.record) && def.canCreate({ actor, data, parent: parent.record });
    return canContributeTo(actor, parent.def, parent.record);
  }
  return def.canCreate ? def.canCreate({ actor, data }) : true;
}

/**
 * Viewer-independent description of an audience (used in exports and
 * notifications). The UI uses a viewer-aware variant ("Only me", "You and Sam").
 */
export function describeAudience(rec: Pick<AccessFields, 'audience' | 'sharedWith'>, nameOf: (userId: string) => string): string {
  switch (rec.audience) {
    case 'private':
      return 'Only the owner';
    case 'adults':
      return 'All grown-ups in the family';
    case 'family':
      return 'Everyone in the family';
    case 'selected': {
      const names = rec.sharedWith.map(nameOf);
      return names.length === 0 ? 'Only the owner' : `The owner and ${names.join(', ')}`;
    }
  }
}
